/**
 * Ownership resolution for the active conversation.
 *
 * Sharing is owner-only, so `isCurrentConversationOwner` gates the Share
 * affordances in `chat-conversation.tsx`, `chat-panel.tsx` and
 * `CrosstabChatPanel.tsx`.
 *
 * Ownership used to be a single mutable boolean that only ever became `true`
 * inside `loadConversationMessages`. The URL-sync effect in `context.tsx`
 * skips that load for a conversation the client just created (the URL and
 * `currentConversationId` already agree), so the flag stayed `false` for the
 * whole session and the Share button never appeared in a new chat — BT-2010.
 *
 * It is now derived per conversation id from two inputs:
 *
 * - `serverOwnerById` — what the API told us, keyed by conversation. Absent
 *   means it has never told us.
 * - `createdConversationIds` — conversations this client started, which the
 *   user owns by definition (the backend stamps `orgUserId` from the sender).
 *
 * The server's answer always wins. That veto is what makes the optimistic
 * claim safe: an internal user may open any conversation (the API allows it
 * for `isInternalUser`), and loading it records `false` here, so a later send
 * into that thread cannot upgrade them to owner.
 */
export function resolveConversationOwnership({
    currentConversationId,
    serverOwnerById,
    createdConversationIds,
}: {
    currentConversationId: string | null;
    serverOwnerById: Readonly<Record<string, boolean>>;
    createdConversationIds: ReadonlySet<string>;
}): boolean {
    if (!currentConversationId) return false;
    return (
        serverOwnerById[currentConversationId] ?? createdConversationIds.has(currentConversationId)
    );
}
